import {Component, OnInit} from '@angular/core';
import {SchoolingTuition, SchoolingTuitionDetails} from '@shared/models/schooling-tuition.model';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Store} from '@ngxs/store';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslationService} from '@shared/services/translation.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SchoolingTuitionService} from '@shared/services/schooling-tuition.service';
import {filter, tap} from 'rxjs/operators';
import {DataResponse} from '@shared/models/response.model';
import {SetCurrentBreadcrumb} from '@shared/store/breadcrumb/breadcrumb.actions';
import {AuthState} from '@shared/store/auth/auth.state';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {SchoolingParticipantService} from '@shared/services/schooling-participant.service';
import {
  SchoolingTuitionParticipantsDialogsComponent
} from '@frontend/modules/schooling/schooling-tuition-participants-dialogs/schooling-tuition-participants-dialogs.component';
import {SchoolingTuitionBookingComponent} from '@shared/components/schooling-tuition-booking/schooling-tuition-booking.component';
import {PermissionKeys} from '@shared/models/permission.model';
import {PermissionService} from '@shared/services/permission.service';
import {findInvalidControls} from '@shared/utils/utils';

@Component({
    selector: 'saf-schooling-tuition-details',
    templateUrl: './schooling-tuition-details.component.html',
    styleUrls: ['./schooling-tuition-details.component.scss'],
    standalone: false
})
export class SchoolingTuitionDetailsComponent implements OnInit {
  public tuitionId: number;
  public employeeId: number;
  public minDate = new Date();
  public topicId: number;
  public topicIdParam: number;
  public employeeIds: any;
  public status: number = 0;
  public assigneeEmployeeId: number;
  public tuitionDetails: SchoolingTuitionDetails;
  public formGroup: UntypedFormGroup;
  public loading: boolean;
  public shouldClose: boolean = true;
  public addParticipants: boolean = false;
  public locationId: number = null;
  public companyId: number = null;
  public canAssign = true ;
  public tabIndex = 0;
  public requiredForm = false;
  public hasPermission = false;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private schoolingTuitionService: SchoolingTuitionService,
    private participantService: SchoolingParticipantService,
    private translationService: TranslationService,
    private snackBar: MatSnackBar,
    private permissionService: PermissionService,
  ) {
    this.hasPermission = this.permissionService.hasPermission(PermissionKeys.SCHOOLING_TUITIONS);
  }

  ngOnInit(): void {

    this.tuitionId = +this.route.snapshot.paramMap.get('id');
    this.topicIdParam = +this.route.snapshot.queryParamMap.get('topicId') || null;
    this.tabIndex = +this.route.snapshot.queryParamMap.get('tabIndex') || null;
    const rawEmployeeIds = this.route.snapshot.queryParamMap.get('employeeIds');

    this.employeeIds = rawEmployeeIds
      ? rawEmployeeIds.split(',').map(id => Number(id)).filter(id => !isNaN(id))
      : [];

    this.employeeId = this.store.selectSnapshot(AuthState.employee) ? this.store.selectSnapshot(AuthState.employee).id : null;
    this.companyId = this.store.selectSnapshot(AuthState.section) ? this.store.selectSnapshot(AuthState.section).id : null;

    if (this.employeeId !== this.assigneeEmployeeId) {
      this.canAssign = false;
    }

    if (this.tuitionId) {
      this.loadForm(this.tuitionId);
    } else {
      this.buildForm(new SchoolingTuitionDetails());
    }
  }

  loadForm(tuitionId) {
    this.loading = true;

    this.schoolingTuitionService.getDetail(tuitionId)
      .pipe(tap(() => this.loading = false))
      .subscribe((tuitionDetails) => {
        this.buildForm(tuitionDetails);

        setTimeout(() => {
          if (tuitionDetails.tuition.externalLecturerId) {
            this.changeValidation(true, false);
          } else {
            this.changeValidation(false, false);
          }
        });
      });
  }

  buildForm(tuitionDetails: SchoolingTuitionDetails) {
    const {tuition} = tuitionDetails;
    const employee = this.store.selectSnapshot(AuthState.employee);

    console.log(this.employeeIds);
    console.log(tuition)

    this.formGroup = new UntypedFormGroup({
      topicId: new UntypedFormControl(this.topicIdParam ? this.topicIdParam : tuition.topicId, Validators.required),
      bookletId: new UntypedFormControl(tuition.bookletId),
      status: new UntypedFormControl(tuition.status),
      tuitionDate: new UntypedFormControl(tuition.tuitionDate),
      tuitionTime: new UntypedFormControl(tuition.tuitionTime  === '00:00:00' ? null : tuition.tuitionTime ),
      validUntilDate: new UntypedFormControl(tuition.validUntilDate),
      maxParticipants: new UntypedFormControl(
        this.employeeIds.length > 0 ? this.employeeIds.length : tuition.maxParticipants === 0 ? null : tuition.maxParticipants
      ),
      participants: new UntypedFormControl(this.employeeIds.length > 0 ? this.employeeIds : null),
      acceptsParticipants: new UntypedFormControl(tuition.acceptsParticipants),
      roomId: new UntypedFormControl(tuition.roomId),
      locationId: new UntypedFormControl(tuition.locationId),
      description: new UntypedFormControl(tuition.description),
      lecturerEmployeeId: new UntypedFormControl(tuition.lecturerEmployeeId),
      externalLecturerId: new UntypedFormControl(tuition.externalLecturerId),
      assigneeEmployeeId: new UntypedFormControl(
        { value: tuition.assigneeEmployeeId ? tuition.assigneeEmployeeId : this.employeeId, disabled: true }, Validators.required),
      organiserCompanyId: new UntypedFormControl(employee ? employee.sectionId : null, Validators.required),
      organiserExternalCompanyId: new UntypedFormControl(tuition.organiserExternalCompanyId),
      created: new UntypedFormControl({ value: tuition.createDate ? tuition.createDate : new Date(), disabled: true}),
    });

    if (this.tuitionId) {
      this.topicId = tuitionDetails.topic.id;
      this.addParticipants = tuitionDetails.tuition.acceptsParticipants;
      this.assigneeEmployeeId = tuitionDetails.tuition.assigneeEmployeeId;
      this.store.dispatch(new SetCurrentBreadcrumb(tuitionDetails.topic.name));
    }

    if (this.employeeIds) {
      this.formGroup.get('maxParticipants')?.setValidators([Validators.min(this.employeeIds.length)]);

    }

    if (tuitionDetails.participants) {
      this.formGroup.get('maxParticipants')?.setValidators([Validators.min(tuitionDetails.participants.length)]);

    }

    this.tuitionDetails = tuitionDetails;
    this.status = this.tuitionDetails.tuition.status;
    this.disableInputs();
  }

  submitForm() {
    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    if (this.formGroup.valid && this.formGroup.get('topicId').value) {
      if (this.formGroup.get('tuitionTime').value === null) {
        this.formGroup.get('tuitionTime').setValue('00:00:00');
      }
      const request = this.tuitionId
        ? this.schoolingTuitionService.update(this.tuitionId, this.formGroup.getRawValue())
        : this.schoolingTuitionService.create(this.formGroup.getRawValue());

      this.loading = true;

      request
        .pipe(tap(() => this.loading = false))
        .subscribe((result) => this.handleResult(result));
    } else {
      this.snackBar.open(this.translationService.translate('requiredFields'));
    }
  }

  handleResult(response: DataResponse<SchoolingTuition>) {
    if (!response.success) {
      this.snackBar.open(this.translationService.translate('saveTuitionError'));
      return;
    }

    if (this.formGroup.get('participants').value !== null) {
      this.employeeIds = this.formGroup.get('participants').value;
    }
    if (this.employeeIds) {

      const employee = this.store.selectSnapshot(AuthState.employee);

      const params = {
        employeeIds: this.employeeIds,
        tuitionId: response.data.id,
        status: 1,
        assigneeEmployeeId: employee ? employee.id : 0,
        // need this for backend participants overwrite online schooling
        overwrite: true
      };

      this.loading = true;

      this.participantService.createMultiple(params)
        .pipe(tap(() => this.loading = false))
        .subscribe(() => {
          this.tuitionDetails.tuition = response.data;
          this.store.dispatch(new SetCurrentBreadcrumb(this.tuitionDetails.topic.name));
          this.snackBar.open(this.translationService.translate('saveTuitionSuccess'));

          if (this.shouldClose) {
            this.closeForm();
          } else {
            this.reloadForm(response.data.id);
          }
        });
    } else {
      this.tuitionDetails.tuition = response.data;
      this.store.dispatch(new SetCurrentBreadcrumb(this.tuitionDetails.topic.name));
      this.snackBar.open(this.translationService.translate('saveTuitionSuccess'));

      if (this.shouldClose) {
        this.closeForm();
      } else {
        this.reloadForm(response.data.id);
      }
    }

  }

  setCloseFlag(shouldClose) {
    this.shouldClose = shouldClose;
  }

  closeForm() {
    this.router.navigate(['..'], {relativeTo: this.route});
  }

  reloadForm(tuitionId: number) {
    this.router.navigate(['..', tuitionId], {relativeTo: this.route});
  }

  openPrint() {
    window.open(this.schoolingTuitionService.getPrintURL(this.tuitionId, localStorage.getItem('jwt')), '_blank');
  }

  openTopicPrint() {
    window.open(this.schoolingTuitionService.printTopic(
      this.tuitionDetails.tuition.bookletId,
      localStorage.getItem('jwt')
    ), '_blank');
  }

  finish() {
    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    const now = new Date();
    const tuitionDateTime = new Date(`${this.tuitionDetails.tuition.tuitionDate}T${this.tuitionDetails.tuition.tuitionTime}`);

    if (tuitionDateTime > now) {
      return this.snackBar.open(this.translationService.translate('dateIsInTheFuture'));
    }

    this.requiredForm = true;
    this.formGroup.get('tuitionDate').setValidators(Validators.required);
    this.formGroup.get('tuitionDate').updateValueAndValidity();

    this.formGroup.get('tuitionTime').setValidators(Validators.required);
    this.formGroup.get('tuitionTime').updateValueAndValidity();

    if (this.formGroup.valid) {
      const dialogConfig: MatDialogConfig = {
        panelClass: 'table-dialog',
        disableClose: true,
        data: {
          tuitionId: this.tuitionId,
          topicId: this.formGroup.get('topicId').value,
          assigneeEmployeeId: this.formGroup.get('assigneeEmployeeId').value
        }
      };

      this.dialog.open(SchoolingTuitionParticipantsDialogsComponent, dialogConfig)
        .afterClosed()
        .pipe(filter(success => success))
        .subscribe(() => {
          this.schoolingTuitionService.finish(this.tuitionDetails.tuition.id)
            .subscribe(() => window.location.reload());
        });
    }

  }

  open() {
    if (!this.hasPermission) {
      return this.returnPermissionError();
    }

    const dialogConfig: MatDialogConfig = {
      panelClass: 'table-dialog',
      disableClose: true,
      data: {
        tuitionId: this.tuitionId,
        topicId: this.formGroup.get('topicId').value,
        assigneeEmployeeId: this.formGroup.get('assigneeEmployeeId').value
      }
    };

    this.dialog.open(SchoolingTuitionParticipantsDialogsComponent, dialogConfig)
      .afterClosed()
      .pipe(filter(success => success))
      .subscribe(() => {
        this.router.navigate(['schooling/booklet/' + this.tuitionDetails.tuition.bookletId], {
          queryParams: {
            tuitionId: this.tuitionDetails.tuition.id,
          }
        });
      });
  }

  disableInputs() {
    if (this.tuitionDetails.tuition.status === 1) {
      this.formGroup.get('description').disable();
      this.formGroup.get('maxParticipants').disable();
    }
  }

  changeValidation(checked: boolean, resetValue: boolean = true) {

    console.log('test')
    const employeeCtrl = this.formGroup.get('lecturerEmployeeId');
    const externalCtrl = this.formGroup.get('externalLecturerId');
    const companyCtrl = this.formGroup.get('organiserExternalCompanyId');

    if (resetValue) {
      employeeCtrl.setValue(null, { emitEvent: false });
      externalCtrl.setValue(null, { emitEvent: false });
      companyCtrl.setValue(null, { emitEvent: false });
    }

    if (!checked) {
      employeeCtrl.setValidators(Validators.required);
      externalCtrl.clearValidators();
      companyCtrl.clearValidators();
    } else {
      employeeCtrl.clearValidators();
      externalCtrl.setValidators(Validators.required);
      companyCtrl.setValidators(Validators.required);
    }

    [employeeCtrl, externalCtrl, companyCtrl].forEach(control => {
      control.updateValueAndValidity({ emitEvent: true, onlySelf: false });
    });
  }

  getLocation(locationId: number) {
    if (locationId) {
      this.locationId = locationId;
    }
  }

  getCompany(companyId: number) {
    if (companyId) {
      this.companyId = companyId;
    }
  }

  getMaxParticipants($event: any) {
    this.employeeIds = this.formGroup.get('participants').value;
    this.formGroup.get('maxParticipants')?.setValidators([Validators.min(this.employeeIds.length)]);
    this.formGroup.get('maxParticipants').setValue(this.employeeIds.length);
  }

  getTopicId(topic: any) {
    this.topicId = topic.id;

    const dialogConfig: MatDialogConfig = {
      panelClass: 'table-dialog',
      disableClose: true,
      data: {
        topicId: this.topicId,
        onlyForDetails: true
      }
    };

    const params = {
      filter: {
        showInternal: 1,
        showExternal: 1,
        topicId: this.topicId,
        statusText: 'openAndOverdue',
      },
    };

    this.schoolingTuitionService.getListDetail(params)
      .pipe(tap(() => this.loading = false))
      .subscribe((response) => {
        if (response.total > 0) {
          this.dialog.open(SchoolingTuitionBookingComponent, dialogConfig)
            .afterClosed()
            .subscribe(() => {
            });
        }
      });
  }

  returnPermissionError() {
    return this.snackBar.open(this.translationService.translate('permissionError'));
  }
}
